/**
 * Calendar & Diary — Wave 4: the Calendar Settings dialog (Rules +
 * Autoresponses), opened from the diary header gear.
 *
 * Rules: timezone, operating hours (the engine-pinned JS-weekday shape —
 * the UI edits exactly that, all-closed sends `null`), buffer / minimum
 * notice / per-day cap (0 = not enforced), and AI booking guidelines. The
 * guidelines save today but only take effect with the AI booking feature in
 * a later wave — the helper copy says so honestly.
 *
 * Autoresponses: agent-owned templates (trigger × channel). The active
 * switch is an optimistic PATCH with rollback; WhatsApp rows carry the
 * honest "queued — template approval pending" hint because deliveries are
 * queued server-side. Deletes confirm through the same Dialog pattern as
 * the diary's Cancel-event flow.
 */
import { useEffect, useState } from 'react'
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react'
import { api } from '@/api/client'
import type {
  AutoresponseChannel,
  AutoresponseTemplate,
  AutoresponseTrigger,
  CalendarOperatingHours,
  CalendarSettings,
} from '@/api/calendar'
import { useToast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { t, type DashboardLocale } from '@/pages/agent/dashboard/copy'
import { jsWeekdayForRow, weekdayLabelsFor } from './shared'

type LoadState = 'loading' | 'error' | 'ready'

/** One operating-hours row in the dialog's Monday-start display order. */
interface DayRow {
  open: boolean
  start: string
  end: string
}

const FALLBACK_TIMEZONES = [
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Qatar',
  'Asia/Kuwait',
  'Asia/Bahrain',
  'Asia/Muscat',
  'Africa/Cairo',
  'Asia/Beirut',
  'Europe/London',
  'UTC',
]

/** Mirrors AccountPage: full ICU list when available, curated fallback otherwise. */
function timezoneOptions(): string[] {
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.(
      'timeZone',
    )
    if (supported?.length) return supported
  } catch {
    /* ignore */
  }
  return FALLBACK_TIMEZONES
}

const DEFAULT_SETTINGS: CalendarSettings = {
  timezone: 'UTC',
  operating_hours: null,
  buffer_minutes: 0,
  min_notice_minutes: 0,
  max_events_per_day: 0,
  ai_guidelines: '',
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  return String(err)
}

function rowsFromHours(hours: CalendarOperatingHours | null): DayRow[] {
  return Array.from({ length: 7 }, (_, i) => {
    const win = hours?.[String(jsWeekdayForRow(i))]
    return win ? { open: true, start: win.start, end: win.end } : { open: false, start: '09:00', end: '18:00' }
  })
}

const TIME_RE = /^\d{2}:\d{2}$/

export function CalendarSettingsDialog({
  open,
  onOpenChange,
  locale,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  locale: DashboardLocale
}) {
  const { addToast } = useToast()
  const [tab, setTab] = useState<'rules' | 'templates'>('rules')

  const [settingsState, setSettingsState] = useState<LoadState>('loading')
  const [templatesState, setTemplatesState] = useState<LoadState>('loading')
  const [templates, setTemplates] = useState<AutoresponseTemplate[]>([])
  const [settingsLoadKey, setSettingsLoadKey] = useState(0)
  const [templatesLoadKey, setTemplatesLoadKey] = useState(0)

  const [timezone, setTimezone] = useState(DEFAULT_SETTINGS.timezone)
  const [rows, setRows] = useState<DayRow[]>(() => rowsFromHours(null))
  const [buffer, setBuffer] = useState(String(DEFAULT_SETTINGS.buffer_minutes))
  const [minNotice, setMinNotice] = useState(String(DEFAULT_SETTINGS.min_notice_minutes))
  const [maxPerDay, setMaxPerDay] = useState(String(DEFAULT_SETTINGS.max_events_per_day))
  const [aiGuidelines, setAiGuidelines] = useState(DEFAULT_SETTINGS.ai_guidelines)

  const [hoursError, setHoursError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setSettingsState('loading')
    api
      .getCalendarSettings()
      .then((res) => {
        if (cancelled) return
        const settings = res?.settings ?? DEFAULT_SETTINGS
        setTimezone(settings.timezone || DEFAULT_SETTINGS.timezone)
        setRows(rowsFromHours(settings.operating_hours))
        setBuffer(String(settings.buffer_minutes ?? 0))
        setMinNotice(String(settings.min_notice_minutes ?? 0))
        setMaxPerDay(String(settings.max_events_per_day ?? 0))
        setAiGuidelines(settings.ai_guidelines ?? '')
        setHoursError(null)
        setSaveError(null)
        setSettingsState('ready')
      })
      .catch(() => {
        if (cancelled) return
        setSettingsState('error')
      })
    return () => {
      cancelled = true
    }
  }, [open, settingsLoadKey])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setTemplatesState('loading')
    api
      .getAutoresponseTemplates()
      .then((res) => {
        if (cancelled) return
        setTemplates(Array.isArray(res?.templates) ? res.templates : [])
        setTemplatesState('ready')
      })
      .catch(() => {
        if (cancelled) return
        setTemplates([])
        setTemplatesState('error')
      })
    return () => {
      cancelled = true
    }
  }, [open, templatesLoadKey])

  const weekdayLabels = weekdayLabelsFor(locale)

  const setRow = (index: number, patch: Partial<DayRow>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)))
  }

  const saveRules = async () => {
    if (saving) return
    const hours: CalendarOperatingHours = {}
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (!row.open) continue
      if (!TIME_RE.test(row.start) || !TIME_RE.test(row.end) || row.end <= row.start) {
        setHoursError(t('calendar.form.endsAfter', locale))
        return
      }
      hours[String(jsWeekdayForRow(i))] = { start: row.start, end: row.end }
    }
    setHoursError(null)
    setSaving(true)
    try {
      const { settings } = await api.putCalendarSettings({
        timezone: timezone || DEFAULT_SETTINGS.timezone,
        operating_hours: Object.keys(hours).length > 0 ? hours : null,
        buffer_minutes: Math.max(0, parseInt(buffer, 10) || 0),
        min_notice_minutes: Math.max(0, parseInt(minNotice, 10) || 0),
        max_events_per_day: Math.max(0, parseInt(maxPerDay, 10) || 0),
        ai_guidelines: aiGuidelines,
      })
      setTimezone(settings.timezone)
      setRows(rowsFromHours(settings.operating_hours))
      setBuffer(String(settings.buffer_minutes))
      setMinNotice(String(settings.min_notice_minutes))
      setMaxPerDay(String(settings.max_events_per_day))
      setAiGuidelines(settings.ai_guidelines)
      setSaveError(null)
      addToast({ title: t('calendar.settings.saved', locale), variant: 'success' })
    } catch (err) {
      setSaveError(`${t('calendar.settings.saveError', locale)} ${errorMessage(err)}`)
    } finally {
      setSaving(false)
    }
  }

  const numberField = (
    id: string,
    labelKey: Parameters<typeof t>[0],
    value: string,
    onChange: (v: string) => void,
  ) => (
    <div>
      <Label htmlFor={id}>{t(labelKey, locale)}</Label>
      <Input
        id={id}
        type="number"
        min={0}
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="mt-1 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
        {t('calendar.settings.notEnforced', locale)}
      </p>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('calendar.settings.title', locale)}</DialogTitle>
        </DialogHeader>

        <div
          role="tablist"
          aria-label={t('calendar.settings.title', locale)}
          className="inline-flex rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface-sunken)] p-0.5"
        >
          {(
            [
              ['rules', 'calendar.settings.tab.rules'],
              ['templates', 'calendar.settings.tab.templates'],
            ] as const
          ).map(([value, key]) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={tab === value}
              onClick={() => setTab(value)}
              className={cn(
                'min-h-tap rounded-[var(--lc-radius-sm)] px-3 transition-colors',
                tab === value
                  ? 'bg-[var(--lc-surface-raised)] text-[var(--lc-text-primary)] shadow-[var(--lc-elevation-sm)]'
                  : 'text-[var(--lc-text-muted)] hover:text-[var(--lc-text-primary)]',
              )}
              style={{ font: 'var(--lc-type-caption)' }}
            >
              {t(key, locale)}
            </button>
          ))}
        </div>

        {tab === 'rules' ? (
          settingsState === 'loading' ? (
            <StateBlock testId="settings-loading" messageKey="calendar.settings.loading" locale={locale} loading />
          ) : settingsState === 'error' ? (
            <div className="flex flex-col items-center gap-[var(--lc-space-sm)]" data-testid="settings-error">
              <p className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }}>
                {t('calendar.settings.error', locale)}
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => setSettingsLoadKey((k) => k + 1)}>
                {t('calendar.settings.retry', locale)}
              </Button>
            </div>
          ) : (
            <div className="space-y-[var(--lc-space-md)]">
              <div>
                <Label htmlFor="cal-settings-tz">{t('calendar.settings.timezone', locale)}</Label>
                <select
                  id="cal-settings-tz"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="mt-1 flex min-h-tap w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-3 py-2 text-sm text-[var(--lc-text-primary)]"
                >
                  {timezoneOptions().map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </div>

              <fieldset>
                <legend className="mb-1" style={{ font: 'var(--lc-type-body-sm)' }}>
                  {t('calendar.settings.operatingHours', locale)}
                </legend>
                <p className="mb-2 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
                  {t('calendar.settings.operatingHours.helper', locale)}
                </p>
                <ul className="space-y-[var(--lc-space-2xs)]">
                  {weekdayLabels.map((label, i) => {
                    const row = rows[i]
                    return (
                      <li
                        key={label}
                        data-testid={`oh-row-${jsWeekdayForRow(i)}`}
                        className="flex min-h-tap flex-wrap items-center gap-[var(--lc-space-sm)]"
                      >
                        <label className="flex min-h-tap w-24 cursor-pointer items-center gap-[var(--lc-space-2xs)]">
                          <input
                            type="checkbox"
                            checked={row.open}
                            onChange={(e) => setRow(i, { open: e.target.checked })}
                            aria-label={`${label} · ${t('calendar.settings.day.open', locale)}`}
                          />
                          <span style={{ font: 'var(--lc-type-body-sm)' }}>{label}</span>
                        </label>
                        <Input
                          type="time"
                          aria-label={t('calendar.settings.start', locale)}
                          value={row.start}
                          disabled={!row.open}
                          onChange={(e) => setRow(i, { start: e.target.value })}
                          className="w-28"
                        />
                        <Input
                          type="time"
                          aria-label={t('calendar.settings.end', locale)}
                          value={row.end}
                          disabled={!row.open}
                          onChange={(e) => setRow(i, { end: e.target.value })}
                          className="w-28"
                        />
                      </li>
                    )
                  })}
                </ul>
                {hoursError ? (
                  <p className="mt-1 text-[var(--lc-status-unpublished-fg)]" style={{ font: 'var(--lc-type-caption)' }}>
                    {hoursError}
                  </p>
                ) : null}
              </fieldset>

              {numberField('cal-settings-buffer', 'calendar.settings.buffer', buffer, setBuffer)}
              {numberField('cal-settings-notice', 'calendar.settings.minNotice', minNotice, setMinNotice)}
              {numberField('cal-settings-cap', 'calendar.settings.maxPerDay', maxPerDay, setMaxPerDay)}

              <div>
                <Label htmlFor="cal-settings-ai">{t('calendar.settings.aiGuidelines', locale)}</Label>
                <textarea
                  id="cal-settings-ai"
                  value={aiGuidelines}
                  onChange={(e) => setAiGuidelines(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  className="flex w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-3 py-2 text-sm text-[var(--lc-text-primary)] placeholder:text-[var(--lc-text-muted)] focus-visible:outline-none"
                />
                <p className="mt-1 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
                  {t('calendar.settings.aiGuidelines.helper', locale)}
                </p>
              </div>

              {saveError ? (
                <p
                  role="alert"
                  data-testid="settings-save-error"
                  className="rounded-[var(--lc-radius-sm)] border border-[var(--lc-status-unpublished-fg)] px-2 py-1 text-[var(--lc-status-unpublished-fg)]"
                  style={{ font: 'var(--lc-type-caption)' }}
                >
                  {saveError}
                </p>
              ) : null}
            </div>
          )
        ) : (
          <TemplatesTab
            state={templatesState}
            templates={templates}
            setTemplates={setTemplates}
            onRetry={() => setTemplatesLoadKey((k) => k + 1)}
            locale={locale}
            addToast={addToast}
          />
        )}

        {tab === 'rules' && settingsState === 'ready' ? (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              {t('calendar.action.cancel', locale)}
            </Button>
            <Button type="button" onClick={() => void saveRules()} disabled={saving}>
              {saving ? <Loader2 className="me-1 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {t('calendar.settings.save', locale)}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function StateBlock({
  testId,
  messageKey,
  locale,
  loading,
}: {
  testId: string
  messageKey: Parameters<typeof t>[0]
  locale: DashboardLocale
  loading?: boolean
}) {
  return (
    <div className="flex min-h-32 flex-col items-center justify-center gap-[var(--lc-space-sm)]" data-testid={testId}>
      {loading ? <Loader2 className="h-5 w-5 animate-spin text-[var(--lc-text-muted)]" aria-hidden="true" /> : null}
      <p className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }}>
        {t(messageKey, locale)}
      </p>
    </div>
  )
}

const TRIGGERS: AutoresponseTrigger[] = ['on_booked', 'on_reschedule', 'on_cancel']
const CHANNELS: AutoresponseChannel[] = ['whatsapp', 'in_app', 'email']

function TemplatesTab({
  state,
  templates,
  setTemplates,
  onRetry,
  locale,
  addToast,
}: {
  state: LoadState
  templates: AutoresponseTemplate[]
  setTemplates: (updater: (prev: AutoresponseTemplate[]) => AutoresponseTemplate[]) => void
  onRetry: () => void
  locale: DashboardLocale
  addToast: (toast: { title: string; description?: string; variant: 'success' | 'error' | 'default' }) => void
}) {
  const [formOpen, setFormOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [trigger, setTrigger] = useState<AutoresponseTrigger>('on_booked')
  const [channel, setChannel] = useState<AutoresponseChannel>('whatsapp')
  const [body, setBody] = useState('')
  const [formError, setFormError] = useState<string | null>(null)
  const [formBusy, setFormBusy] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<AutoresponseTemplate | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  const resetForm = () => {
    setFormOpen(false)
    setEditingId(null)
    setName('')
    setTrigger('on_booked')
    setChannel('whatsapp')
    setBody('')
    setFormError(null)
  }

  const startEdit = (tpl: AutoresponseTemplate) => {
    setFormOpen(true)
    setEditingId(tpl.id)
    setName(tpl.name)
    setTrigger(tpl.trigger)
    setChannel(tpl.channel)
    setBody(tpl.body)
    setFormError(null)
  }

  const submit = async () => {
    if (formBusy) return
    if (!name.trim()) {
      setFormError(t('calendar.form.required', locale))
      return
    }
    if (!body.trim()) {
      setFormError(t('calendar.form.required', locale))
      return
    }
    setFormError(null)
    setFormBusy(true)
    try {
      if (editingId) {
        const { template } = await api.updateAutoresponseTemplate(editingId, {
          name: name.trim(),
          trigger,
          channel,
          body: body.trim(),
        })
        setTemplates((prev) => prev.map((tpl) => (tpl.id === template.id ? template : tpl)))
      } else {
        const { template } = await api.createAutoresponseTemplate({
          name: name.trim(),
          trigger,
          channel,
          body: body.trim(),
          active: true,
        })
        setTemplates((prev) => [...prev, template])
      }
      addToast({ title: t('calendar.templates.created', locale), variant: 'success' })
      resetForm()
    } catch (err) {
      addToast({
        title: t('calendar.templates.createError', locale),
        description: errorMessage(err),
        variant: 'error',
      })
    } finally {
      setFormBusy(false)
    }
  }

  /** Optimistic active flip; a rejection rolls the row back and says so. */
  const toggleActive = async (tpl: AutoresponseTemplate) => {
    const next = !tpl.active
    setTemplates((prev) => prev.map((row) => (row.id === tpl.id ? { ...row, active: next } : row)))
    try {
      const { template } = await api.updateAutoresponseTemplate(tpl.id, { active: next })
      setTemplates((prev) => prev.map((row) => (row.id === tpl.id ? template : row)))
    } catch (err) {
      setTemplates((prev) => prev.map((row) => (row.id === tpl.id ? tpl : row)))
      addToast({
        title: t('calendar.templates.toggleError', locale),
        description: errorMessage(err),
        variant: 'error',
      })
    }
  }

  const confirmDelete = async () => {
    if (!deleteTarget || deleteBusy) return
    setDeleteBusy(true)
    try {
      await api.deleteAutoresponseTemplate(deleteTarget.id)
      setTemplates((prev) => prev.filter((tpl) => tpl.id !== deleteTarget.id))
      addToast({ title: t('calendar.templates.deleted', locale), variant: 'success' })
      setDeleteTarget(null)
    } catch (err) {
      addToast({
        title: t('calendar.templates.deleteError', locale),
        description: errorMessage(err),
        variant: 'error',
      })
    } finally {
      setDeleteBusy(false)
    }
  }

  if (state === 'loading') {
    return <StateBlock testId="templates-loading" messageKey="calendar.templates.loading" locale={locale} loading />
  }
  if (state === 'error') {
    return (
      <div className="flex flex-col items-center gap-[var(--lc-space-sm)]" data-testid="templates-error">
        <p className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }}>
          {t('calendar.templates.error', locale)}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          {t('calendar.settings.retry', locale)}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-[var(--lc-space-md)]">
      {templates.length === 0 && !formOpen ? (
        <p className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-body-sm)' }} data-testid="templates-empty">
          {t('calendar.templates.empty', locale)}
        </p>
      ) : (
        <ul>
          {templates.map((tpl) => (
            <li
              key={tpl.id}
              data-testid={`template-${tpl.id}`}
              className="flex min-h-tap flex-wrap items-center gap-[var(--lc-space-sm)] border-b border-[var(--lc-border)] py-[var(--lc-space-2xs)]"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate" style={{ font: 'var(--lc-type-body-sm)' }}>
                  {tpl.name}
                </span>
                <span className="block text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
                  {t(`calendar.templates.trigger.${tpl.trigger}` as Parameters<typeof t>[0], locale)} ·{' '}
                  {t(`calendar.templates.channel.${tpl.channel}` as Parameters<typeof t>[0], locale)}
                  {tpl.channel === 'whatsapp' ? (
                    <>
                      {' · '}
                      <span data-testid={`template-${tpl.id}-queued`}>
                        {t('calendar.templates.whatsappQueued', locale)}
                      </span>
                    </>
                  ) : null}
                </span>
              </span>
              <label className="flex min-h-tap cursor-pointer items-center gap-[var(--lc-space-2xs)]">
                <input
                  type="checkbox"
                  checked={tpl.active}
                  onChange={() => void toggleActive(tpl)}
                  aria-label={`${tpl.name} · ${t('calendar.templates.active', locale)}`}
                />
                <span className="text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
                  {t('calendar.templates.active', locale)}
                </span>
              </label>
              <button
                type="button"
                aria-label={`${tpl.name} · ${t('calendar.templates.edit', locale)}`}
                title={t('calendar.templates.edit', locale)}
                onClick={() => startEdit(tpl)}
                className="inline-flex h-tap min-h-tap w-tap min-w-tap items-center justify-center rounded-[var(--lc-radius-md)] text-[var(--lc-text-muted)] transition-colors hover:bg-[var(--lc-surface-sunken)] hover:text-[var(--lc-text-primary)]"
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                aria-label={`${tpl.name} · ${t('calendar.templates.delete', locale)}`}
                title={t('calendar.templates.delete', locale)}
                onClick={() => setDeleteTarget(tpl)}
                className="inline-flex h-tap min-h-tap w-tap min-w-tap items-center justify-center rounded-[var(--lc-radius-md)] text-[var(--lc-text-muted)] transition-colors hover:bg-[var(--lc-surface-sunken)] hover:text-[var(--lc-text-primary)]"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {formOpen ? (
        <div className="space-y-[var(--lc-space-md)] rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] p-[var(--lc-space-sm)]">
          <div>
            <Label htmlFor="cal-tpl-name">{t('calendar.templates.name', locale)}</Label>
            <Input id="cal-tpl-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="cal-tpl-trigger">{t('calendar.templates.trigger', locale)}</Label>
            <select
              id="cal-tpl-trigger"
              value={trigger}
              onChange={(e) => setTrigger(e.target.value as AutoresponseTrigger)}
              className="mt-1 flex min-h-tap w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-3 py-2 text-sm text-[var(--lc-text-primary)]"
            >
              {TRIGGERS.map((value) => (
                <option key={value} value={value}>
                  {t(`calendar.templates.trigger.${value}` as Parameters<typeof t>[0], locale)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="cal-tpl-channel">{t('calendar.templates.channel', locale)}</Label>
            <select
              id="cal-tpl-channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value as AutoresponseChannel)}
              className="mt-1 flex min-h-tap w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-3 py-2 text-sm text-[var(--lc-text-primary)]"
            >
              {CHANNELS.map((value) => (
                <option key={value} value={value}>
                  {t(`calendar.templates.channel.${value}` as Parameters<typeof t>[0], locale)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor="cal-tpl-body">{t('calendar.templates.body', locale)}</Label>
            <textarea
              id="cal-tpl-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              className="flex w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-3 py-2 text-sm text-[var(--lc-text-primary)] placeholder:text-[var(--lc-text-muted)] focus-visible:outline-none"
            />
            <p className="mt-1 text-[var(--lc-text-muted)]" style={{ font: 'var(--lc-type-caption)' }}>
              {t('calendar.templates.body.hint', locale)}
            </p>
          </div>
          {formError ? (
            <p className="text-[var(--lc-status-unpublished-fg)]" style={{ font: 'var(--lc-type-caption)' }}>
              {formError}
            </p>
          ) : null}
          <div className="flex gap-[var(--lc-space-sm)]">
            <Button type="button" variant="outline" onClick={resetForm} disabled={formBusy}>
              {t('calendar.action.cancel', locale)}
            </Button>
            <Button type="button" onClick={() => void submit()} disabled={formBusy}>
              {formBusy ? <Loader2 className="me-1 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {t('calendar.templates.save', locale)}
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" variant="outline" size="sm" onClick={() => setFormOpen(true)}>
          <Plus className="me-1 h-4 w-4" aria-hidden="true" />
          {t('calendar.templates.new', locale)}
        </Button>
      )}

      <Dialog open={deleteTarget !== null} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('calendar.templates.deleteTitle', locale)}</DialogTitle>
          </DialogHeader>
          {deleteTarget ? (
            <p className="text-[var(--lc-text-secondary)]" style={{ font: 'var(--lc-type-body-sm)' }}>
              {t('calendar.templates.deleteBody', locale, { name: deleteTarget.name })}
            </p>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteBusy}>
              {t('calendar.action.cancel', locale)}
            </Button>
            <Button type="button" variant="destructive" disabled={deleteBusy} onClick={() => void confirmDelete()}>
              {deleteBusy ? <Loader2 className="me-1 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              {t('calendar.templates.delete', locale)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
